import Dexie from 'dexie';
import DB from "./db.js";
import { buildMessageSearchText } from './messageSearch.js';
import { applyGroupMessageRemoval } from '../api/groupMessageCleanup.js';

const DB_NAME_PREFIX = 'im-web-';
const DB_USER_ID_REQUIRED_ERROR = 'DB_USER_ID_REQUIRED';

function normalizeDbUserId(userId) {
	if (userId === undefined || userId === null || userId === '') {
		return '';
	}
	return String(userId).trim();
}

class ImIndexedDB extends DB {
	constructor() {
		super();
		this.db = null;
	}

	open(userId) {
		const normalizedUserId = normalizeDbUserId(userId);
		if (!normalizedUserId) {
			const error = new Error('打开本地数据库失败：userId 为空');
			error.code = DB_USER_ID_REQUIRED_ERROR;
			throw error;
		}
		const dbName = `${DB_NAME_PREFIX}${normalizedUserId}`;
		if (this.db && this.db.name === dbName) {
			this.userId = normalizedUserId;
			return;
		}
		if (this.db) {
			this.db.close();
			this.db = null;
		}
		this.userId = normalizedUserId;
		this.db = new Dexie(dbName);
		this.db.version(4).stores({
			// 配置表
			config: 'key',
			// 会话信息
			conversations: 'key',
			// 消息表
			messages: 'localId,id,[convKey+seqNo+sendTime]',
			// 好友表
			friends: 'id',
			// 群表
			groups: 'id'
		});
		this.db.version(5).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,convKey,sendTime,seqNo,[convKey+sendTime+localId],[convKey+id],[convKey+seqNo],[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id'
		});
		this.db.version(6).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,convKey,sendTime,seqNo,[convKey+sendTime+localId],[convKey+id],[convKey+seqNo],[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id'
		});
		this.db.version(7).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,convKey,sendTime,seqNo,[convKey+sendTime+localId],[convKey+id],[convKey+seqNo],[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id',
			groupMembers: '[groupId+userId],groupId,userId,version,snapshotAt,[groupId+version]'
		});
		this.db.version(8).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,convKey,sendTime,seqNo,[convKey+sendTime+localId],[convKey+id],[convKey+seqNo],[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id',
			groupMembers: '[groupId+userId],groupId,userId,version,snapshotAt,[groupId+version]',
			friendGroups: 'id'
		});
		this.db.version(9).stores({
			config: 'key',
			conversations: 'key',
			conversationStates: 'key',
			messages: 'localId,id,convKey,sendTime,seqNo,[convKey+sendTime+localId],[convKey+id],[convKey+seqNo],[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id',
			groupMembers: '[groupId+userId],groupId,userId,version,snapshotAt,[groupId+version]',
			friendGroups: 'id'
		}).upgrade((transaction) => Promise.all([
			transaction.table('friends').toCollection().modify((friend) => {
				delete friend.isDnd;
				delete friend.isTop;
			}),
			transaction.table('groups').toCollection().modify((group) => {
				delete group.isDnd;
				delete group.isTop;
			}),
			transaction.table('conversations').toCollection().modify((conversation) => {
				delete conversation.isDnd;
				delete conversation.isTop;
			})
		]));
	}

	close() {
		if (this.db) {
			this.db.close();
			this.db = null;
		}
		this.userId = null;
	}


	async loadAllConversations() {
		return this.db.conversations.toArray();
	}

	async loadAllConversationStates() {
		return this.db.conversationStates.toArray();
	}

	async deleteConversationByKey(convKey) {
		return this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
			await this.db.conversations.delete(convKey);
			await this.db.messages.where("convKey").equals(convKey).delete();
		});
	}

	async findConversationByKey(key) {
		return this.db.conversations.get(key);
	}

	async saveConversation(conversation) {
		return this.db.conversations.put(conversation);
	}

	async saveConversationAndMessage(conversations, messages) {
		return this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
			await this.db.conversations.bulkPut(conversations);
			await this.db.messages.bulkPut(messages);
		})
	}

	/**
	 * 向上加载某个会话的历史消息
	 * @param {string} convKey   会话key
	 * @param {number} minSeqNo    最小消息序号
	 * @param {object} maxSeqNo  最大消息序号
	 */
	async findPageMessage(convKey, minSeqNo, maxSeqNo) {
		// 构建查询范围
		const lower = [convKey, minSeqNo, 0];
		const upper = [convKey, maxSeqNo, Infinity];
		const messages = await this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between(lower, upper, true, true) // 后面两个true是包含边界值
			.toArray();
		return messages;
	}

	async deleteMessageByLocalId(localId) {
		return await this.db.messages.delete(localId);
	}

	async deleteMessageByConvKey(convKey) {
		return await this.db.messages.where("convKey").equals(convKey).delete();
	}

	async saveMessage(message) {
		return this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
			const conversation = await this.db.conversations.get(message.convKey);
			if (conversation && conversation.type === 'GROUP') {
				applyGroupMessageRemoval(message, conversation.messageRemovalCutoffs);
			}
			return this.db.messages.put(message);
		});
	}

	async findMessageById(messageId) {
		return await this.db.messages.where('id').equals(messageId).first();
	}

	async findMessageByLocalId(localId) {
		return await this.db.messages.where('localId').equals(localId).first();
	}

	async findMessageByConvKey(convKey) {
		return this.db.messages.where("convKey").equals(convKey).toArray();
	}

	async findRecentMessages(convKey, limit = 30) {
		const size = Math.max(1, limit || 30);
		const messages = await this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between([convKey, Dexie.minKey, Dexie.minKey], [convKey, Dexie.maxKey, Dexie.maxKey], true, true)
			.reverse()
			.limit(size)
			.toArray();
		return messages.reverse();
	}

	async findMessagesBefore(convKey, beforeSeqNo, limit = 30) {
		const size = Math.max(1, limit || 30);
		const upperSeqNo = Number(beforeSeqNo);
		if (!Number.isFinite(upperSeqNo) || upperSeqNo <= 0) {
			return this.findRecentMessages(convKey, size);
		}
		const messages = await this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between([convKey, Dexie.minKey, Dexie.minKey], [convKey, upperSeqNo - 1, Dexie.maxKey], true, true)
			.reverse()
			.limit(size)
			.toArray();
		return messages.reverse();
	}

	async findMessagesByTypes(convKey, types, beforeSeqNo, limit = 30, keyword = '') {
		const typeSet = new Set((Array.isArray(types) ? types : []).map(Number));
		const before = Number(beforeSeqNo);
		const size = Math.max(1, Math.floor(Number(limit) || 30));
		const normalizedKeyword = String(keyword || '').trim().toLowerCase();
		const upperSeqNo = Number.isFinite(before) && before > 0 ? before : Dexie.maxKey;
		let collection = this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between([convKey, Dexie.minKey, Dexie.minKey],
				[convKey, upperSeqNo, Dexie.minKey], true, false)
			.reverse();
		collection = collection.filter(message =>
			Number.isFinite(Number(message.seqNo)) && Number(message.seqNo) > 0 &&
			(!typeSet.size || typeSet.has(Number(message.type))) &&
			(!normalizedKeyword || buildMessageSearchText(message).toLowerCase().includes(normalizedKeyword)));
		return collection.limit(size).toArray();
	}

	async upsertChatSnapshot(snapshot) {
		return this.persistChatMutations({ snapshots: [snapshot] });
	}

	async persistChatMutations(payload = {}) {
		const clearedConversationKeys = Array.isArray(payload.clearedConversationKeys) ? payload.clearedConversationKeys : [];
		const deletedLocalIds = Array.isArray(payload.deletedLocalIds) ? payload.deletedLocalIds : [];
		const deletedConversationKeys = Array.isArray(payload.deletedConversationKeys) ? payload.deletedConversationKeys : [];
		const snapshots = Array.isArray(payload.snapshots) ? payload.snapshots : [];
		const conversationStates = Array.isArray(payload.conversationStates) ? payload.conversationStates : [];
		const configs = Array.isArray(payload.configs) ? payload.configs : [];
		return this.db.transaction('rw', this.db.config, this.db.conversations, this.db.conversationStates, this.db.messages, async () => {
			const removalSnapshots = [];
			for (const snapshot of snapshots) {
				const conversation = snapshot && snapshot.conversation;
				if (!conversation || conversation.type !== 'GROUP') continue;
				const cutoffs = conversation.messageRemovalCutoffs || {};
				if (!Object.keys(cutoffs).length) continue;
				(snapshot.messages || []).forEach(message => applyGroupMessageRemoval(message, cutoffs));
				const previous = await this.db.conversations.get(conversation.key);
				if (JSON.stringify(previous && previous.messageRemovalCutoffs || {}) !== JSON.stringify(cutoffs)) {
					removalSnapshots.push(conversation);
				}
			}
			if (payload.replaceConversationStates === true) {
				await this.db.conversationStates.clear();
			}
			if (clearedConversationKeys.length) {
				await Promise.all(clearedConversationKeys.map((convKey) =>
					this.db.messages.where("convKey").equals(convKey).delete()
				));
			}
			if (deletedLocalIds.length) {
				await this.db.messages.bulkDelete(deletedLocalIds);
			}
			if (deletedConversationKeys.length) {
				await Promise.all(deletedConversationKeys.map((convKey) =>
					Promise.all([
						this.db.conversations.delete(convKey),
						this.db.messages.where("convKey").equals(convKey).delete()
					])
				));
			}
			const conversations = [];
			const messages = [];
			snapshots.forEach((snapshot) => {
				if (snapshot && snapshot.conversation && snapshot.conversation.key) {
					conversations.push(snapshot.conversation);
				}
				if (snapshot && Array.isArray(snapshot.messages)) {
					messages.push(...snapshot.messages);
				}
			});
			if (conversations.length) {
				await this.db.conversations.bulkPut(conversations);
			}
			if (messages.length) {
				await this.db.messages.bulkPut(messages);
			}
			// 扫描整个指定会话，覆盖未加载进消息窗口的历史与引用；与游标保存处于同一事务。
			for (const conversation of removalSnapshots) {
				await this.db.messages.where('convKey').equals(conversation.key).modify(message => {
					applyGroupMessageRemoval(message, conversation.messageRemovalCutoffs);
				});
			}
			if (conversationStates.length) {
				await this.db.conversationStates.bulkPut(conversationStates);
			}
			if (configs.length) {
				await this.db.config.bulkPut(configs.filter(config => config && config.key));
			}
		});
	}

	// 查询引用了这条消息的消息
	async findQuoteMessages(message) {
		const lower = [message.convKey, message.seqNo, message.sendTime];
		const upper = [message.convKey, Infinity, Infinity];
		return await this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between(lower, upper)
			.and(item => item.quoteMessage && item.quoteMessage.id == message.id)
			.toArray();
	}

	async findAllFriends() {
		return await this.db.friends.toArray();
	}

	async saveFriends(friends) {
		await this.db.friends.bulkPut(friends);
	}

	async saveFriend(friend) {
		return await this.db.friends.put(friend);
	}

	async syncAllFriends(friends) {
		return this.db.transaction('rw', this.db.config, this.db.friends, async () => {
			const config = {
				key: 'lastSyncFriendTime',
				val: new Date().getTime()
			}
			await this.db.config.put(config);
			await this.db.friends.clear();
			await this.db.friends.bulkAdd(friends);
		});
	}

	async findLastSyncFriendsTime(friends) {
		const config = await this.db.config.where('key').equals('lastSyncFriendTime').first();
		return config ? config.val : 0;
	}

	async findAllGroups() {
		return await this.db.groups.toArray();
	}

	async saveGroups(groups) {
		await this.db.groups.bulkPut(groups);
	}

	async saveGroup(group) {
		return await this.db.groups.put(group);
	}

	async syncAllGroups(groups) {
		return this.db.transaction('rw', this.db.config, this.db.groups, async () => {
			const config = {
				key: 'lastSyncGroupTime',
				val: new Date().getTime()
			}
			await this.db.config.put(config);
			await this.db.groups.clear();
			await this.db.groups.bulkAdd(groups);
		});
	}

	async findLastSyncGroupsTime(groups) {
		const config = await this.db.config.where('key').equals('lastSyncGroupTime').first();
		return config ? config.val : 0;
	}

	normalizeGroupMemberRecord(groupId, member) {
		if (!member || member.userId === undefined || member.userId === null) {
			return null;
		}
		const record = { ...member };
		record.groupId = groupId;
		record.snapshotAt = Date.now();
		delete record.checked;
		delete record.locked;
		delete record.hide;
		delete record.online;
		return record;
	}

	async findGroupMembers(groupId) {
		return await this.db.groupMembers
			.where('groupId')
			.equals(groupId)
			.toArray();
	}

	async saveGroupMembers(groupId, members) {
		const records = (Array.isArray(members) ? members : [])
			.map(member => this.normalizeGroupMemberRecord(groupId, member))
			.filter(Boolean);
		if (!records.length) {
			return;
		}
		await this.db.groupMembers.bulkPut(records);
	}

	async syncGroupMembers(groupId, members) {
		const records = (Array.isArray(members) ? members : [])
			.map(member => this.normalizeGroupMemberRecord(groupId, member))
			.filter(Boolean);
		return this.db.transaction('rw', this.db.config, this.db.groupMembers, async () => {
			await this.db.groupMembers.where('groupId').equals(groupId).delete();
			if (records.length) {
				await this.db.groupMembers.bulkPut(records);
			}
			await this.db.config.put({
				key: `lastSyncGroupMembersTime:${groupId}`,
				val: new Date().getTime()
			});
		});
	}

	async deleteGroupMembers(groupId) {
		return this.db.groupMembers.where('groupId').equals(groupId).delete();
	}

	async removeGroupMembersByUserIds(groupId, userIds) {
		if (groupId === undefined || groupId === null) {
			return;
		}
		const ids = (Array.isArray(userIds) ? userIds : [])
			.filter((userId) => userId !== undefined && userId !== null && userId !== '');
		if (!ids.length) {
			return;
		}
		const keys = ids.map((userId) => [groupId, userId]);
		await this.db.groupMembers.bulkDelete(keys);
	}

	async findAllFriendGroups() {
		return await this.db.friendGroups.toArray();
	}

	async saveFriendGroups(groups) {
		const records = (Array.isArray(groups) ? groups : []).filter((group) => group && group.id !== undefined && group.id !== null);
		if (!records.length) {
			return;
		}
		await this.db.friendGroups.bulkPut(records);
	}

	async saveFriendGroup(group) {
		if (!group || group.id === undefined || group.id === null) {
			return;
		}
		return this.db.friendGroups.put(group);
	}

	async deleteFriendGroup(groupId) {
		if (groupId === undefined || groupId === null || groupId === '') {
			return;
		}
		return this.db.friendGroups.delete(groupId);
	}

	async syncAllFriendGroups(groups) {
		const records = (Array.isArray(groups) ? groups : []).filter((group) => group && group.id !== undefined && group.id !== null);
		return this.db.transaction('rw', this.db.config, this.db.friendGroups, async () => {
			await this.db.friendGroups.clear();
			if (records.length) {
				await this.db.friendGroups.bulkPut(records);
			}
			await this.db.config.put({
				key: 'lastSyncFriendGroupTime',
				val: new Date().getTime()
			});
		});
	}

	async saveConfig(key, val) {
		return this.db.config.put({ key, val });
	}

	async findConfig(key) {
		const config = await this.db.config.where('key').equals(key).first();
		return config ? config.val : undefined;
	}

	async deleteConfig(key) {
		return this.db.config.delete(key);
	}

	async clearLocalCache() {
		return this.db.transaction(
			'rw',
				this.db.config,
				this.db.conversations,
				this.db.conversationStates,
				this.db.messages,
			this.db.friends,
			this.db.groups,
			this.db.groupMembers,
			this.db.friendGroups,
			async () => {
				await Promise.all([
						this.db.config.clear(),
						this.db.conversations.clear(),
						this.db.conversationStates.clear(),
						this.db.messages.clear(),
					this.db.friends.clear(),
					this.db.groups.clear(),
					this.db.groupMembers.clear(),
					this.db.friendGroups.clear()
				]);
			}
		);
	}
}


export default ImIndexedDB;
